function BlankLine({ wide }: { wide?: boolean }) {
  return (
    <span
      className="inline-block border-b border-gray-400"
      style={{ width: wide ? '14rem' : '10rem' }}
    />
  );
}

export function BlankPrintableWaiver() {
  return (
    <div className="printable-waiver relative z-20 max-w-4xl mx-auto bg-white p-8 print:p-4 text-sm text-gray-800">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 print:mb-6">
        <div className="flex flex-col">
          <div className="mb-2">
            <h2 className="text-xl font-semibold print:text-lg">Bright Minds Collective LLC</h2>
            <p className="text-gray-600 text-sm">Student Waiver · Bright Horizon Chinese School and Kaleido</p>
          </div>
          <p className="text-gray-600">650 Shell Blvd</p>
          <p className="text-gray-600">Foster City, CA 94404</p>
          <p className="text-gray-600">Phone: 650-620-0299</p>
          <p className="text-gray-600">Email: billing@bhcskids.com</p>
        </div>
        <div className="text-right">
          <p className="text-gray-500 text-sm">Date: <BlankLine /></p>
        </div>
      </div>

      {/* Student */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Student</h3>
        <div>
          <span className="text-gray-600">Name: </span><BlankLine wide />
        </div>
      </div>

      {/* Contacts */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Contacts</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Parent/Guardian 1</div>
            <div className="space-y-3">
              <div><span className="text-gray-600">Name: </span><BlankLine wide /></div>
              <div><span className="text-gray-600">Relationship: </span><BlankLine /></div>
              <div><span className="text-gray-600">Phone: </span><BlankLine /></div>
              <div><span className="text-gray-600">Email: </span><BlankLine wide /></div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Parent/Guardian 2 <span className="font-normal text-gray-500">(optional)</span></div>
            <div className="space-y-3">
              <div><span className="text-gray-600">Name: </span><BlankLine wide /></div>
              <div><span className="text-gray-600">Relationship: </span><BlankLine /></div>
              <div><span className="text-gray-600">Phone: </span><BlankLine /></div>
              <div><span className="text-gray-600">Email: </span><BlankLine wide /></div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4 col-span-2">
            <div className="font-semibold text-gray-900 mb-2">Emergency Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-600">Name: </span><BlankLine wide /></div>
              <div><span className="text-gray-600">Relationship: </span><BlankLine /></div>
              <div><span className="text-gray-600">Phone: </span><BlankLine /></div>
              <div><span className="text-gray-600">Email: </span><BlankLine wide /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Health & Insurance */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Health &amp; Insurance</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Allergies</div>
            <div className="space-y-2">
              <div>
                <span className="text-gray-600">Has allergies: </span>
                <span className="mr-4">&#9634; No</span>
                <span>&#9634; Yes</span>
              </div>
              <div className="text-gray-600 text-xs mt-1">If yes, please list and explain:</div>
              <div className="space-y-5 mt-2">
                <div className="border-b border-gray-400" />
                <div className="border-b border-gray-400" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Physician <span className="font-normal text-gray-500">(optional)</span></div>
            <div className="space-y-3">
              <div><span className="text-gray-600">Name: </span><BlankLine wide /></div>
              <div><span className="text-gray-600">Phone: </span><BlankLine /></div>
              <div><span className="text-gray-600">Address: </span><BlankLine wide /></div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4 col-span-2">
            <div className="font-semibold text-gray-900 mb-2">Insurance <span className="font-normal text-gray-500">(optional)</span></div>
            <div className="grid grid-cols-3 gap-3">
              <div><span className="text-gray-600">Provider: </span><BlankLine /></div>
              <div><span className="text-gray-600">Policy #: </span><BlankLine /></div>
              <div><span className="text-gray-600">Group #: </span><BlankLine /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Field Trip */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Field Trip &amp; Off-Site Activity Permission &amp; Release</h3>
        <p className="text-gray-700 mb-2">
          I give permission for my child(ren), <BlankLine wide />, to participate in any field trips, outdoor activities, or off-site excursions organized by Bright Minds Collective LLC during the school year afterschool programs, seasonal programs, or Kaleido programs. I release and discharge the staff, teachers, owners, and volunteers of Bright Minds Collective LLC from all liabilities arising out of or in connection with such activities.
        </p>
        <p className="text-gray-700">
          Liability includes all claims, demands, losses, causes of action, suits, or judgments of any and every kind that I, my heirs, executors, administrators, or assignees may have against Bright Minds Collective LLC, including any resulting from injury, illness, property damage, or death occurring during such activities.
        </p>
      </div>

      {/* Authorizations */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Authorization &amp; Consent</h3>
        <div className="space-y-4">
          {[
            {
              label: 'Emergency Medical Authorization',
              text: 'In the event of an emergency, I authorize Bright Minds Collective LLC staff to take appropriate action for the safety and well-being of my child, including seeking emergency medical treatment and arranging emergency transportation if necessary. I understand that every effort will be made to contact me. If my child\'s physician cannot be reached, I consent to care from qualified emergency medical personnel, including hospital staff and licensed physicians. I accept responsibility for any and all costs incurred in obtaining such care.',
            },
            {
              label: 'Liability Waiver',
              text: 'I release and hold harmless Bright Minds Collective LLC, its directors, staff, and volunteers from any and all liability for injuries, illnesses, or damages sustained by my child during school-related and other program activities, including on-site instruction, off-site events, and transportation.',
            },
            {
              label: 'Assumption of Risk',
              text: 'I understand that participation in school, extracurricular, and early-learning activities involves inherent risks. I assume full responsibility for these risks on behalf of my child.',
            },
            {
              label: 'Behavior Policy',
              text: 'I acknowledge that my child is expected to follow the rules and behavioral guidelines of Bright Minds Collective LLC and its programs. Bright Minds Collective LLC may suspend or dismiss any student for unsafe or inappropriate behavior without refund.',
            },
            {
              label: 'Photo Release',
              text: 'I acknowledge and agree that Bright Minds Collective LLC may take photographs and videos during lessons, activities, camps, and events for the purpose of marketing and promotional use.',
            },
            {
              label: 'Infectious Disease Disclaimer',
              text: 'I understand that despite reasonable safety precautions, exposure to infectious diseases (including COVID-19) may still occur. I agree not to hold Bright Minds Collective LLC responsible in such an event.',
            },
          ].map(({ label, text }) => (
            <div key={label} className="flex items-start gap-2">
              <span className="mt-0.5 text-base shrink-0">&#9634;</span>
              <span>
                <span className="font-medium">{label}:</span>
                {' '}{text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Signature */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Signature</h3>
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2">
            <div className="mb-6">
              <div className="text-gray-600 text-xs mb-1">Parent/Guardian Name (Printed)</div>
              <div className="border-b border-gray-400 h-7" />
            </div>
            <div className="mb-6">
              <div className="text-gray-600 text-xs mb-1">Signature</div>
              <div className="border-b border-gray-400 h-7" />
            </div>
          </div>
          <div>
            <div className="mb-6">
              <div className="text-gray-600 text-xs mb-1">Date</div>
              <div className="border-b border-gray-400 h-7" />
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          By signing above, I confirm that I have read, understood, and agree to all sections of this waiver.
        </p>
      </div>
    </div>
  );
}
